import { PAGE_FILE_SRC_ATTRIBUTE, isPageFileUrl } from "./PageFileUrl";

import MarkdownIt from "markdown-it";

type IsPageFile = (url: string) => boolean;

const IMG_SRC = /(<img\b[^>]*?\s)src\s*=\s*(["'])(.*?)\2/gi;

/**
 * Raw HTML `<img src="…/PageFiles/…">` (markdown-it runs with `html: true`) becomes `<img data-ark-src="…">`, so the browser
 * does not request the attachment without a token. Other images are left alone.
 */
export function rewritePageFileImgTags(html: string, isPageFile: IsPageFile = (url) => isPageFileUrl(url)): string {
    if (!html || html.toLowerCase().indexOf("<img") < 0) return html;
    return html.replace(IMG_SRC, (all: string, head: string, quote: string, src: string) =>
        isPageFile(decodeEntities(src)) ? `${head}${PAGE_FILE_SRC_ATTRIBUTE}=${quote}${src}${quote}` : all
    );
}

function decodeEntities(value: string): string {
    return value.replace(/&amp;/g, "&").replace(/&quot;/g, '"').replace(/&#39;/g, "'");
}

/**
 * markdown-it plugin (OrbitalPlan/18 phase 0): images that point at Ark attachments render with `data-ark-src` instead of
 * `src`; `PageFileResolver` then loads them with the bearer token. The check runs at render time, so the backend URL can
 * come from the runtime configuration loaded after the engine was built.
 */
export function pageFileImages(md: MarkdownIt, isPageFile: IsPageFile = (url) => isPageFileUrl(url)): void {
    const image = md.renderer.rules.image ?? ((tokens, idx, options, _env, self) => self.renderToken(tokens, idx, options));
    md.renderer.rules.image = (tokens, idx, options, env, self) => {
        const token = tokens[idx];
        const src = token.attrGet("src");
        if (src && isPageFile(src)) {
            token.attrs = (token.attrs ?? []).filter(([name]) => name !== "src");
            token.attrSet(PAGE_FILE_SRC_ATTRIBUTE, src);
        }
        return image(tokens, idx, options, env, self);
    };

    for (const rule of ["html_inline", "html_block"]) {
        const original = md.renderer.rules[rule];
        md.renderer.rules[rule] = (tokens, idx, options, env, self) =>
            rewritePageFileImgTags(original ? original(tokens, idx, options, env, self) : tokens[idx].content, isPageFile);
    }
}
